import {ChangeDetectionStrategy, Component, inject, viewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {BrunataOnlineLogoComponentComponent} from '@shared/brunata-online-logo-component/brunata-online-logo-component.component';
import {
    ActivatedRoute,
    NavigationEnd,
    Router,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {
    BuildingServices,
    FeatureFlagService,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {filter, map, shareReplay, startWith} from 'rxjs';
import {CookieService} from 'ngx-cookie-service';
import {take} from 'rxjs/operators';
import {NgbDropdown, NgbDropdownModule} from '@ng-bootstrap/ng-bootstrap';
import {Options} from '@popperjs/core';
import {BreadcrumbComponent} from 'src/app/breadcrumb/breadcrumb.component';
import {getLastChild} from '@shared/route-utils';
import {ACROSS_SYSTEMS_EXPORT_SERVICES} from 'src/app/export/routes';
import {selectUser} from 'src/app/state/selectors';
import {Store} from '@ngrx/store';
import {UserStateSuccess} from 'src/app/state/reducers';
import {loadUser} from 'src/app/state/actions';
import {environment} from 'src/environments/environment';
import {Minenergi2Component} from 'src/app/minenergi2/minenergi2.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {INSTALLATION_PROJECTS_FEATURE_FLAG} from 'src/app/installation-projects/installation-projects.model';
import {ModalInputService} from '@brunata-angular/ui';
import {SearchModalComponent} from 'src/app/search/search-modal/search-modal.component';

@Component({
    selector: 'app-main',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        BrunataOnlineLogoComponentComponent,
        RouterOutlet,
        RouterLink,
        TranslatePipe,
        RouterLinkActive,
        NgbDropdownModule,
        BreadcrumbComponent,
        Minenergi2Component,
    ],
    templateUrl: './main.component.html',
    styleUrl: './main.component.scss',
})
export class MainComponent {
    protected readonly userMenu = viewChild(NgbDropdown);
    protected environment = environment.env;
    protected readonly buildingServices = BuildingServices;

    private readonly http = inject(HttpClient);
    private readonly store = inject(Store);
    private readonly cookieService = inject(CookieService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly modalInputService = inject(ModalInputService);
    public readonly translateService = inject(TranslateService);

    protected readonly installationProjectsEnabled$ = this.featureFlagService.isFeatureEnabled$(
        INSTALLATION_PROJECTS_FEATURE_FLAG,
    );

    protected readonly searchEnabled$ = this.featureFlagService.isFeatureEnabled$('AGI-17951');

    readonly skipBreadcrumb$ = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.getSkipBreadcrumb(this.activatedRoute)),
    );

    readonly user$ = this.store.select(selectUser).pipe(
        filter((userState): userState is UserStateSuccess => userState.status === 'success'),
        map(userState => userState.user),
        map(user => ({
            ...user,
            helpLink: this.translateService.translate('common_HELP_LINK', 'online', 'common'),
            hasAcrossSystemsExport: ACROSS_SYSTEMS_EXPORT_SERVICES.some(service =>
                user.buildingServices.includes(service),
            ),
        })),
        shareReplay(1),
    );

    constructor() {
        this.store.dispatch(loadUser());
        this.user$.pipe(take(1)).subscribe(() => {
            // When the user has been fetched, an authentication token must have been saved in
            // a cookie. We also set the token as a cookie called token for backwards compatability with
            // react frontend.
            if (this.cookieService.check('access_token')) {
                this.cookieService.set('token', this.cookieService.get('access_token'), {
                    path: '/',
                    expires: 1 / 24, // Expires in one hour
                });
            }
            // Get and save cookie for backwards compatability with visual and compass.
            this.http.post(`${environment.onlineServiceUrl}/user/cookie`, null).subscribe();
        });

        this.user$.pipe(takeUntilDestroyed()).subscribe(user => {
            this.translateService.setLanguage(user.language);
        });
    }

    noOffsetPopperOptions(options: Partial<Options>): Partial<Options> {
        options.modifiers?.push({
            name: 'offset',
            options: {
                offset: [0, 0],
            },
        });
        return options;
    }

    triggerMinEnergi2(): void {
        window.open(environment.enityEmsUrl, '_blank');
    }

    protected openSearch(): void {
        this.modalInputService.open(
            SearchModalComponent,
            {query: ''},
            {size: 'xl', scrollable: true},
        );
    }

    private getSkipBreadcrumb(route: ActivatedRoute): boolean {
        const skipBreadcrumb: unknown = getLastChild(route).snapshot.data['skipBreadcrumb'];
        if (typeof skipBreadcrumb === 'boolean') {
            return skipBreadcrumb;
        }
        return false;
    }
}
